import { flow, types } from 'mobx-state-tree';
import {
  getAudiobooks,
  getSingleAudiobook,
  addSingleAudiobook,
  updateSingleAudiobook,
  deleteSingleAudiobook,
  getAudiobooksForImport,
  getAudiobookDetailsForImport,
} from '../services/audiobookApi';
import { composeError } from '../utils/transforms';
import { AudiobookResponse, Audiobook, Category, Tag, TargetGroup, Chapter } from '../types';
import { withRootStore } from './withRootStore';

const ChapterModel = types.model('ChapterModel', {
  id: types.number,
  name: types.string,
  src: types.string,
  chapterNumber: types.number,
  duration: types.optional(types.maybeNull(types.number), null),
  state: types.optional(types.string, ''),
  published: types.optional(types.boolean, false),
});

const ImportedChapterModel = types.model('ImportedChapterModel', {
  name: types.string,
  chapterNumber: types.number,
  src: types.string,
  duration: types.optional(types.maybeNull(types.number), null),
});

const ImportedAudiobookModel = types.model('ImportedAudiobookModel', {
  name: types.string,
  author: types.string,
  publisher: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  releaseDate: types.maybeNull(types.string),
  releaseYear: types.maybeNull(types.number),
  language: types.maybeNull(types.string),
  image: types.maybeNull(types.string),
  reader: types.maybeNull(types.string),
  shopUrl: types.maybeNull(types.string),
  ebook: types.maybeNull(types.string),
  pdfBook: types.maybeNull(types.string),
  chapters: types.optional(types.array(ImportedChapterModel), []),
});

const AudiobookModel = types.model('AudiobookModel', {
  id: types.optional(types.number, -1),
  name: types.string,
  image: types.maybeNull(types.string),
  description: types.string,
  releaseDate: types.maybeNull(types.string),
  author: types.string,
  publisher: types.string,
  releaseYear: types.maybeNull(types.number),
  language: types.maybeNull(types.string),
  reader: types.maybeNull(types.string),
  shopUrl: types.maybeNull(types.string),
  isbn: types.maybeNull(types.string),
  ebook: types.maybeNull(types.string),
  ebookStartSkip: types.maybeNull(types.number),
  ebookEndSkip: types.maybeNull(types.number),
  pdfBook: types.maybeNull(types.string),
  totalDurationSeconds: types.maybeNull(types.number),
  calculatedTotalDurationSeconds: types.maybeNull(types.number),
  totalDurationOverrideSeconds: types.maybeNull(types.number),
  mediaTypeId: types.optional(types.number, -1),
  chapters: types.optional(types.array(ChapterModel), []),
  categoryIds: types.optional(types.array(types.number), []),
  tagIds: types.optional(types.array(types.number), []),
  targetGroupIds: types.optional(types.array(types.number), []),
  published: types.optional(types.boolean, false),
  isTimeTracked: types.optional(types.boolean, false),
});

export const AudiobookStore = types
  .model({
    audiobooks: types.optional(types.array(AudiobookModel), []),
    importableAudiobooks: types.optional(types.frozen(), []),
    selectedAudiobook: types.maybeNull(AudiobookModel),
    selectedImportedAudiobook: types.maybeNull(ImportedAudiobookModel),
    loading: false,
    importLoading: false,
  })
  .extend(withRootStore)
  .actions((self) => {
    const getAllAudiobooks = flow(function* () {
      try {
        const response = yield getAudiobooks();
        self.audiobooks = response.data.map((book: AudiobookResponse) => ({
          ...book,
          // These are not used where getAllAudiobooks is called
          // mediaTypeId: book.mediaType.id,
          // categoryIds: book.categories?.map((data: Category) => data.id),
          // tagIds: book.tags?.map((data: Tag) => data.id),
          // targetGroupIds: book.targetGroups?.map((data: TargetGroup) => data.id),
        }));
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(error);
      }
    });

    const getImportableAudiobooks = flow(function* () {
      self.importLoading = true;
      try {
        const response = yield getAudiobooksForImport();
        self.importableAudiobooks = response.data;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      } finally {
        self.importLoading = false;
      }
    });

    const getSelectedAudiobook = flow(function* (id: string, isImport = false) {
      self.loading = true;
      try {
        if (isImport) {
          const response = yield getAudiobookDetailsForImport(id);
          self.selectedImportedAudiobook = response.data;
        } else {
          const response = yield getSingleAudiobook(id);
          console.log('--- getSelectedAudiobook Response ---', JSON.stringify(response.data, null, 2));
          self.selectedAudiobook = {
            ...response.data,
            isTimeTracked: response.data.isTimeTracked,
            isbn: response.data.isbn ?? null,
            totalDurationSeconds: response.data.totalDurationSeconds ?? null,
            calculatedTotalDurationSeconds: response.data.calculatedTotalDurationSeconds ?? null,
            totalDurationOverrideSeconds: response.data.totalDurationOverrideSeconds ?? null,
            mediaTypeId: response.data.mediaType.id,
            chapters: response.data.chapters?.map((chapter: Chapter) => ({
              id: chapter.id,
              name: chapter.name,
              src: chapter.src,
              chapterNumber: chapter.chapterNumber,
              duration: (chapter as unknown as { duration?: number | null })?.duration ?? null,
              state: '',
              published: false,
            })),
            categoryIds: response.data.categories?.map((data: Category) => data.id),
            tagIds: response.data.tags?.map((data: Tag) => data.id),
            targetGroupIds: response.data.targetGroups?.map((data: TargetGroup) => data.id),
          };
        }

        // Sort chapters
        if (self.selectedAudiobook?.chapters) {
          self.selectedAudiobook.chapters.sort((a, b) => a.chapterNumber - b.chapterNumber);
        }
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        console.error(err);
      } finally {
        self.loading = false;
      }
    });

    const createAudiobook = flow(function* (
      audiobook: Audiobook,
      chapters: Chapter[],
      files: File[],
      image?: File | string,
      ebook?: File | string,
      pdfBook?: File | string
    ) {
      self.loading = true;
      try {
        const response = (yield addSingleAudiobook(audiobook, chapters, files, image, ebook, pdfBook)) as {
          data?: { id?: number | string };
          id?: number | string;
        };
        self.rootStore.errorStore.setSuccess('save_success');
        if (self.selectedImportedAudiobook) {
          self.selectedImportedAudiobook = null;
        }
        return response?.data ?? response ?? null;
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        return null;
      } finally {
        self.loading = false;
      }
    });

    const updateAudiobook = flow(function* (
      audiobook: Audiobook,
      chapters: Chapter[],
      files: File[],
      image?: File,
      ebook?: File,
      pdfBook?: File
    ) {
      self.loading = true;
      try {
        yield updateSingleAudiobook(audiobook, chapters, files, image, ebook, pdfBook);
        self.rootStore.errorStore.setSuccess('save_success');
        self.loading = false;
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.loading = false;
      }
    });

    const deleteAudiobook = flow(function* (id: string) {
      try {
        yield deleteSingleAudiobook(id);
        self.rootStore.errorStore.setSuccess('delete_success');
      } catch (err) {
        console.error(err);
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
      }
    });

    const clearImportedAudiobook = () => {
      self.selectedImportedAudiobook = null;
    };

    return {
      getAllAudiobooks,
      getSelectedAudiobook,
      createAudiobook,
      updateAudiobook,
      deleteAudiobook,
      getImportableAudiobooks,
      clearImportedAudiobook,
    };
  });
